import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { PublicLayout } from "@/components/Navbar";
import { StockBadge } from "@/components/StockBadge";
import { Minus, Plus, Trash2, ArrowRight, ShoppingCart } from "lucide-react";

export default function Cart() {
  const { cart, setCart, refreshCart } = useCart();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  useEffect(() => { refreshCart(); /* eslint-disable-next-line */ }, []);

  const update = async (sku, quantity) => {
    setBusy(true);
    try {
      const { data } = await api.put("/retailer/cart", { sku, quantity });
      setCart(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); refreshCart(); }
    finally { setBusy(false); }
  };

  const remove = async (sku) => {
    const { data } = await api.delete(`/retailer/cart/${sku}`);
    setCart(data);
  };

  return (
    <PublicLayout>
      <div className="mx-auto max-w-6xl px-4 py-12 sm:px-8">
        <h1 className="font-serif-display text-4xl font-bold text-slate-50">Your <span className="mc-gold-gradient">Cart</span></h1>

        {cart.items.length === 0 ? (
          <div className="mt-12 rounded-2xl border border-[#2A2E39] p-12 text-center text-[#64748B]">
            <ShoppingCart className="mx-auto h-10 w-10 opacity-40" />
            <p className="mt-4">Your cart is empty.</p>
            <Link to="/portal" className="mt-2 inline-block text-[#D4AF37]">Browse the catalogue</Link>
          </div>
        ) : (
          <div className="mt-10 grid gap-8 lg:grid-cols-3">
            <div className="space-y-3 lg:col-span-2">
              {cart.items.map((it) => (
                <div key={it.sku} data-testid={`cart-item-${it.sku}`} className="flex gap-4 rounded-2xl border border-[#2A2E39] bg-[#1A1D24] p-4">
                  <img src={it.image_url} alt={it.name} className="h-20 w-20 rounded-xl object-cover" />
                  <div className="flex flex-1 flex-col">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-semibold text-slate-100">{it.name}</div>
                        <div className="text-xs text-[#94A3B8] font-mono-data">{it.sku} • {it.pack_size}</div>
                      </div>
                      <StockBadge status={it.stock_status} testId={`cart-stock-${it.sku}`} />
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center rounded-full border border-[#2A2E39]">
                        <button data-testid={`cart-dec-${it.sku}`} onClick={() => update(it.sku, it.quantity - 1)} disabled={busy} className="p-2 text-slate-300 hover:text-[#D4AF37]"><Minus className="h-3.5 w-3.5" /></button>
                        <span data-testid={`cart-qty-${it.sku}`} className="w-10 text-center text-sm font-semibold text-slate-100">{it.quantity}</span>
                        <button data-testid={`cart-inc-${it.sku}`} onClick={() => update(it.sku, it.quantity + 1)} disabled={busy} className="p-2 text-slate-300 hover:text-[#D4AF37]"><Plus className="h-3.5 w-3.5" /></button>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="font-bold text-[#D4AF37]">€{it.line_total.toFixed(2)}</div>
                          <div className="text-[11px] text-[#64748B]">€{it.unit_price.toFixed(2)} ea</div>
                        </div>
                        <button data-testid={`cart-remove-${it.sku}`} onClick={() => remove(it.sku)} className="rounded-lg p-2 text-[#94A3B8] hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="h-fit rounded-2xl border border-[#2A2E39] bg-[#1A1D24] p-6">
              <h3 className="text-lg font-semibold text-slate-100">Order Summary</h3>
              <dl className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between text-[#94A3B8]"><dt>Subtotal</dt><dd data-testid="cart-subtotal" className="text-slate-100">€{cart.subtotal.toFixed(2)}</dd></div>
                <div className="flex justify-between text-[#94A3B8]"><dt>VAT</dt><dd data-testid="cart-vat" className="text-slate-100">€{cart.vat_total.toFixed(2)}</dd></div>
                <div className="mt-2 flex justify-between border-t border-[#2A2E39] pt-3 text-base font-bold"><dt className="text-slate-100">Total</dt><dd data-testid="cart-total" className="text-[#D4AF37]">€{cart.total.toFixed(2)}</dd></div>
              </dl>
              <button data-testid="goto-checkout-btn" onClick={() => navigate("/portal/checkout")} className="mc-btn-gold mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5">
                Proceed to Checkout <ArrowRight className="h-4 w-4" />
              </button>
              <p className="mt-3 text-center text-[11px] text-[#64748B]">Stock is re-validated against Sage 50 at checkout.</p>
            </div>
          </div>
        )}
      </div>
    </PublicLayout>
  );
}
